import React, { useState, useEffect, useCallback, useMemo } from "react";
import axiosInstance from "../../axiosInstance";
import GeneralTable from "../../components/Table/GeneralTable";
import StatusCard from "../../components/StatusCard/StatusCard";
import PageHeader from "../../components/ui/PageHeader";
import SummaryCard from "../../components/ui/SummaryCard";
import {
  StatusTabs,
  SelectDropdown,
  FilterToolbar,
} from "../../components/ui/FilterControls";
import { ErrorState } from "../../components/ui/Feedback";
import { parseLocalDate, formatDateLong } from "./leaveUtils";
import "./ListOfHolidays.scss";

const DAY_MS = 24 * 60 * 60 * 1000;

const TYPE_TABS = [
  { key: "mandatory", label: "Mandatory" },
  { key: "optional", label: "Optional" },
  { key: "all", label: "All Holidays" },
];

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const MONTH_OPTIONS = [
  { value: "", label: "All Months" },
  ...MONTHS.map((name, index) => ({ value: String(index + 1), label: name })),
];

const toTitleCase = (text) => text.replace(/\b\w/g, (c) => c.toUpperCase());

// Branch List Component
const BranchList = ({ branches = [] }) => {
  if (!branches.length) {
    return <span className="loh-branch loh-branch--muted">All Branches</span>;
  }
  return (
    <div className="loh-branches">
      {branches.map((branch, index) => (
        <span key={index} className="loh-branch">
          {branch}
        </span>
      ))}
    </div>
  );
};

function ListOfHolidays() {
  const [holidaysData, setHolidaysData] = useState([]);
  const [typeFilter, setTypeFilter] = useState("mandatory");
  const [monthFilter, setMonthFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/holidays/all`,
      );

      if (response.status !== 200) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      const result = response.data;
      if (!result || !result.data) {
        throw new Error("Invalid data format: missing holiday data.");
      }

      setHolidaysData(
        result.data.map((holiday) => {
          const date = parseLocalDate(holiday.eventDate);
          return {
            holidayName: holiday.holidayName,
            eventDate: date ? formatDateLong(date) : "N/A",
            holidayType: (holiday.holidayType || "Optional").toLowerCase(),
            noOfdays: holiday.noOfDays,
            branchNames: [],
            rawDate: holiday.eventDate,
            dateValue: date ? date.getTime() : null,
            month: date ? date.getMonth() + 1 : null,
          };
        }),
      );
    } catch (err) {
      if (err.response) {
        setError(
          `Server Error: ${err.response.status} - ${err.response.statusText}`,
        );
      } else if (err.request) {
        setError("Network error: Unable to fetch holidays.");
      } else {
        setError(err.message || "An error occurred while fetching holidays.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  // ---- statistics ----------------------------------------------------------
  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // a holiday happening today is still upcoming
    const start = today.getTime();
    const end = start + 30 * DAY_MS;

    return {
      total: holidaysData.length,
      mandatory: holidaysData.filter((h) => h.holidayType === "mandatory")
        .length,
      optional: holidaysData.filter((h) => h.holidayType === "optional").length,
      upcoming: holidaysData.filter(
        (h) =>
          h.dateValue !== null && h.dateValue >= start && h.dateValue <= end,
      ).length,
    };
  }, [holidaysData]);

  // ---- filtering / sorting ---------------------------------------------------
  const filtered = useMemo(
    () =>
      holidaysData
        .filter((item) => {
          const matchesType =
            typeFilter === "all" || item.holidayType === typeFilter;
          const matchesMonth =
            monthFilter === "" || item.month === parseInt(monthFilter, 10);
          return matchesType && matchesMonth;
        })
        .sort((a, b) => {
          const order = { mandatory: 1, optional: 2 };
          const byType =
            (order[a.holidayType] || 3) - (order[b.holidayType] || 3);
          // within a type, chronological
          return byType !== 0
            ? byType
            : (a.dateValue || 0) - (b.dateValue || 0);
        }),
    [holidaysData, typeFilter, monthFilter],
  );

  const tabs = TYPE_TABS.map((tab) => ({
    ...tab,
    count:
      tab.key === "all"
        ? stats.total
        : tab.key === "mandatory"
          ? stats.mandatory
          : stats.optional,
  }));

  const isDefaultView = typeFilter === "mandatory" && monthFilter === "";
  const resetView = () => {
    setTypeFilter("mandatory");
    setMonthFilter("");
  };

  const columns = [
    { header: "Holiday Name", accessor: "holidayName" },
    {
      header: "Event Date",
      accessor: "eventDate",
      sortAccessor: (row) => row.dateValue,
    },
    {
      header: "Holiday Type",
      accessor: "holidayType",
      render: (row) => <StatusCard status={toTitleCase(row.holidayType)} />,
    },
    { header: "Number of Days", accessor: "noOfdays", align: "center" },
    {
      header: "Branches",
      accessor: "branchNames",
      sortable: false,
      render: (row) => <BranchList branches={row.branchNames} />,
    },
  ];

  return (
    <div className="holidays-page">
      <PageHeader
        breadcrumbs={[
          { label: "Self Service", to: "/ess/my-leave" },
          { label: "Company Holidays" },
        ]}
        title="Company Holidays"
        subtitle="Holidays recognised by the company, mandatory or optional, which may vary by branch."
      />

      {error ? (
        <section className="hr-card">
          <ErrorState
            title="Couldn't load the holiday list"
            message={error}
            onRetry={fetchHolidays}
          />
        </section>
      ) : (
        <>
          <div className="hr-summary-grid">
            <SummaryCard
              tone="primary"
              icon="calendar_month"
              label="Total Holidays"
              value={stats.total}
              hint="This year's list"
              loading={loading}
            />
            <SummaryCard
              tone="success"
              icon="task_alt"
              label="Mandatory"
              value={stats.mandatory}
              hint="Office closed"
              loading={loading}
            />
            <SummaryCard
              tone="warning"
              icon="event_available"
              label="Optional"
              value={stats.optional}
              hint="Apply as optional leave"
              loading={loading}
            />
            <SummaryCard
              tone="primary"
              icon="event_upcoming"
              label="Upcoming Holidays"
              value={stats.upcoming}
              hint="Within the next 30 days"
              loading={loading}
            />
          </div>

          <FilterToolbar
            tabs={
              <StatusTabs
                tabs={tabs}
                value={typeFilter}
                onChange={setTypeFilter}
                ariaLabel="Filter by holiday type"
              />
            }
            onReset={resetView}
            resetDisabled={isDefaultView}
          >
            <SelectDropdown
              id="holiday-month"
              label="Month"
              value={monthFilter}
              onChange={setMonthFilter}
              options={MONTH_OPTIONS}
            />
          </FilterToolbar>

          <GeneralTable
            columns={columns}
            data={filtered}
            loading={loading}
            className="listofholidays-table"
            title="List Of Holidays"
            emptyStateTitle="No holidays found"
            emptyStateSubtitle="Try a different holiday type or month."
          />
        </>
      )}
    </div>
  );
}

export default ListOfHolidays;
